/**
 * Users API
 * API functions for user management
 */

import { api, type ApiResponse } from '@/lib/api';

// Types
export interface User {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
    status: 'active' | 'invited' | 'draft' | 'suspended' | 'archived';
    role: string | { id: string; name: string; icon?: string } | null;
    token: string | null;
    last_access: string | null;
    last_page: string | null;
    provider: string;
    external_identifier: string | null;
    email_notifications: boolean;
    language: string | null;
    avatar: string | null;
    location: string | null;
    title: string | null;
    description: string | null;
    tags: string[] | null;
}

export interface CreateUserPayload {
    email: string;
    password?: string;
    first_name?: string;
    last_name?: string;
    status?: User['status'];
    role?: string | null;
    provider?: string;
    language?: string;
    avatar?: string;
    location?: string;
    title?: string;
    description?: string;
}

export interface UpdateUserPayload {
    first_name?: string;
    last_name?: string;
    email?: string;
    password?: string;
    status?: User['status'];
    role?: string | null;
    language?: string;
    avatar?: string | null;
    location?: string | null;
    title?: string | null;
    description?: string | null;
}

export interface InviteUserPayload {
    email: string | string[];
    role: string;
    invite_url?: string;
}

export interface AcceptInvitePayload {
    token: string;
    password: string;
}

export interface UsersQuery {
    filter?: Record<string, any>;
    sort?: string[];
    limit?: number;
    offset?: number;
    fields?: string[];
    search?: string;
    meta?: string;
}

// API Functions

/**
 * Get all users
 */
export async function getUsers(query?: UsersQuery): Promise<{ data: User[]; meta?: any }> {
    const params = new URLSearchParams();

    if (query?.filter) {
        params.append('filter', JSON.stringify(query.filter));
    }
    if (query?.sort) {
        query.sort.forEach(s => params.append('sort[]', s));
    }
    if (query?.limit) {
        params.append('limit', String(query.limit));
    }
    if (query?.offset) {
        params.append('offset', String(query.offset));
    }
    if (query?.fields) {
        params.append('fields', query.fields.join(','));
    }
    if (query?.search) {
        params.append('search', query.search);
    }
    if (query?.meta) {
        params.append('meta', query.meta);
    }

    const queryString = params.toString();
    const url = `/users${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<ApiResponse<User[]> & { meta?: any }>(url);
    return { data: response.data, meta: response.meta };
}

/**
 * Get a single user by ID
 */
export async function getUser(id: string, fields?: string[]): Promise<User> {
    const params = new URLSearchParams();
    if (fields) {
        params.append('fields', fields.join(','));
    }

    const queryString = params.toString();
    const url = `/users/${id}${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<ApiResponse<User>>(url);
    return response.data;
}

/**
 * Get current user
 */
export async function getCurrentUser(fields?: string[]): Promise<User> {
    const params = new URLSearchParams();
    if (fields) {
        params.append('fields', fields.join(','));
    }

    const queryString = params.toString();
    const url = `/users/me${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<ApiResponse<User>>(url);
    return response.data;
}

/**
 * Create a new user
 */
export async function createUser(payload: CreateUserPayload): Promise<User> {
    const response = await api.post<ApiResponse<User>>('/users', payload);
    return response.data;
}

/**
 * Update a user
 */
export async function updateUser(id: string, payload: UpdateUserPayload): Promise<User> {
    const response = await api.patch<ApiResponse<User>>(`/users/${id}`, payload);
    return response.data;
}

/**
 * Update current user
 */
export async function updateCurrentUser(payload: UpdateUserPayload): Promise<User> {
    const response = await api.patch<ApiResponse<User>>('/users/me', payload);
    return response.data;
}

/**
 * Delete a user
 */
export async function deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
}

/**
 * Delete multiple users
 */
export async function deleteUsers(ids: string[]): Promise<void> {
    await api.delete('/users', ids);
}

/**
 * Invite users by email
 */
export async function inviteUsers(payload: InviteUserPayload): Promise<void> {
    await api.post('/users/invite', payload);
}

/**
 * Accept user invitation
 */
export async function acceptInvite(payload: AcceptInvitePayload): Promise<void> {
    await api.post('/users/invite/accept', payload);
}

/**
 * Track user's last page
 */
export async function trackPage(lastPage: string): Promise<void> {
    await api.patch('/users/me/track/page', { last_page: lastPage });
}

/**
 * Get user display name
 */
export function getUserDisplayName(user: User | null | undefined): string {
    if (!user) return 'Unknown';

    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || user.email || 'Unknown';
}

/**
 * Get user initials for avatar
 */
export function getUserInitials(user: User | null | undefined): string {
    if (!user) return '?';

    const name = getUserDisplayName(user);
    if (name === 'Unknown' || name === user.email) {
        return user.email?.charAt(0).toUpperCase() || '?';
    }

    return name
        .split(' ')
        .map(n => n.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

/**
 * Get status badge color
 */
export function getStatusColor(status: User['status']): string {
    switch (status) {
        case 'active':
            return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
        case 'invited':
            return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
        case 'draft':
            return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
        case 'suspended':
            return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
        case 'archived':
            return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
        default:
            return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
    }
}
